import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, AlertCircle, Mail, Lock, ShieldCheck, Crown } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login, verifyOTP, isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1); // 1: password, 2: OTP, 3: AMC selection
  const [formData, setFormData] = useState({ email: '', password: '', otp: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [message, setMessage] = useState('');
  const [amcList, setAmcList] = useState([]);
  const [selectedAmcId, setSelectedAmcId] = useState('');

  React.useEffect(() => {
    if (isAuthenticated && user) {
      if (user.user_type === 'owner') {
        navigate('/owner/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Surface session-expired bounce from the global axios interceptor.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('session_expired') === '1') {
        const reason = localStorage.getItem('session_expired_reason') || 'Your session has expired.';
        setMessage(`${reason} Please sign in again.`);
        localStorage.removeItem('session_expired_reason');
      }
    } catch { /* ignore */ }
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await login(formData.email, formData.password);
      setTempToken(response.temp_token);
      setMessage(response.message);
      // Store AMC list from login response for later use
      if (response.amc_list && response.amc_list.length > 0) {
        setAmcList(response.amc_list);
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await verifyOTP(formData.email, formData.otp);
      
      // Check if AMC selection is needed
      if (response.requires_amc_selection) {
        setAmcList(response.amc_list);
        setMessage('Please select an AMC to continue.');
        setStep(3);
        return;
      }
      
      // Single AMC — proceed to dashboard
      if (response.user?.user_type === 'owner') {
        navigate('/owner/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleAMCSelect = async () => {
    if (!selectedAmcId) {
      setError('Please select a portal');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await verifyOTP(formData.email, formData.otp, selectedAmcId);
      if (response.user?.user_type === 'owner' || selectedAmcId === '__owner__') {
        navigate('/owner/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to select portal');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await login(formData.email, formData.password);
      setTempToken(response.temp_token);
      setMessage('New OTP sent to your email');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/prime-accounts-logo.jpg" 
                alt="Prime Accounts Logo" 
                className="h-20 w-auto object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">PrimeAIF Fund Manager</h1>
            <p className="text-gray-600 mt-2">
              {step === 1 ? 'Sign in to your account' : step === 2 ? 'Verify your identity' : 'Select your portal'}
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className={`flex items-center ${step >= 2 ? 'text-green-600' : 'text-blue-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-green-600' : 'bg-blue-600'} text-white font-semibold`}>
                {step >= 2 ? '\u2713' : '1'}
              </div>
              <span className="ml-2 text-sm font-medium">Password</span>
            </div>
            <div className={`mx-3 h-0.5 w-8 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${step === 2 ? 'text-blue-600' : step > 2 ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 2 ? 'bg-blue-600' : step > 2 ? 'bg-green-600' : 'bg-gray-300'} text-white font-semibold`}>
                {step > 2 ? '\u2713' : '2'}
              </div>
              <span className="ml-2 text-sm font-medium">OTP</span>
            </div>
            {amcList.length > 0 && (
              <>
                <div className={`mx-3 h-0.5 w-8 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${step === 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 3 ? 'bg-blue-600' : 'bg-gray-300'} text-white font-semibold`}>3</div>
                  <span className="ml-2 text-sm font-medium">AMC</span>
                </div>
              </>
            )}
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start" data-testid="error-message">
              <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <Mail className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
              <span className="text-green-800 text-sm">{message}</span>
            </div>
          )}

          {/* Step 1: Password */}
          {step === 1 && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>

              <div className="flex items-center justify-end">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium
                  hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                data-testid="login-button"
              >
                {loading ? 'Verifying...' : 'Continue'}
              </button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <div className="text-sm text-gray-600 mb-3">
                  We've sent a 6-digit code to <strong>{formData.email}</strong>
                </div>
                <input
                  type="text"
                  required
                  maxLength="6"
                  value={formData.otp}
                  onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest font-semibold"
                  placeholder="000000"
                  data-testid="otp-input"
                />
                <p className="mt-2 text-xs text-gray-500">
                  OTP is valid for 10 minutes
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || formData.otp.length !== 6}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium
                  hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                data-testid="verify-otp-button"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-gray-600 hover:text-gray-700"
                >
                  ← Back to password
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-700 disabled:text-blue-400"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Portal / AMC Selection */}
          {step === 3 && (
            <div className="space-y-6" data-testid="amc-selection-step">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Portal
                </label>
                <p className="text-sm text-gray-500 mb-4">
                  Your account has access to multiple portals. Please select one to continue.
                </p>
                <div className="space-y-2">
                  {amcList.map((amc) => {
                    const isOwner = amc.amc_id === '__owner__';
                    return (
                      <label
                        key={amc.amc_id}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedAmcId === amc.amc_id
                            ? isOwner ? 'border-amber-500 bg-amber-50' : 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`amc-option-${amc.amc_id}`}
                      >
                        <input
                          type="radio"
                          name="amc_selection"
                          value={amc.amc_id}
                          checked={selectedAmcId === amc.amc_id}
                          onChange={(e) => setSelectedAmcId(e.target.value)}
                          className="text-blue-600"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{amc.amc_name}</div>
                          <div className="text-xs text-gray-500 capitalize">
                            {isOwner ? 'Platform Administration' : `Role: ${amc.role}`}
                          </div>
                        </div>
                        {isOwner ? <Crown size={20} className="text-amber-500" /> : <Building2 size={20} className="text-gray-400" />}
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={handleAMCSelect}
                disabled={loading || !selectedAmcId}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium
                  hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                data-testid="amc-select-button"
              >
                {loading ? 'Loading...' : 'Continue'}
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
